import {
    ArrowUpward as ArrowUp,
    ArrowDownward as ArrowDown,
    Edit,
    Save,
    Delete as Trash2,
    Close as X,
} from "@mui/icons-material";
import {
    Card,
    CardContent,
    CardHeader,
    TextField,
    IconButton,
    Typography,
    Box,
    Avatar,
} from "@mui/material";
import { useState } from "react";

import { DocumentSection } from "../../../../../../models/documents";
import { TReadBudgetsResponse } from "../../../../../../services/budget/read-budgets";
import { TReadBudgetsLevelsDetailsByIdResponse } from "../../../../../../services/budget/read-budgets-levels-detailed-by-id";
import ActivityProfileTable from "./components/tables/ActivityProfileTable";
import ScopeTable from "./components/tables/ScopeTable";

interface ISectionItemProps {
    section: DocumentSection;
    onUpdate: (id: string, updates: Partial<DocumentSection>) => void;
    onDelete: (id: string) => void;
    onMoveUp: (id: string) => void;
    onMoveDown: (id: string) => void;
    isFirst: boolean;
    isLast: boolean;
    tableScopeData: TReadBudgetsLevelsDetailsByIdResponse;
    budgetData: TReadBudgetsResponse;
}

export default function SectionItem({
    section,
    onUpdate,
    onDelete,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    tableScopeData,
    budgetData,
}: ISectionItemProps) {
    const [isEditing, setIsEditing] = useState(false);
    const [title, setTitle] = useState(section.session_title);
    const [content, setContent] = useState(section.content);
    const profiles = budgetData.BudgetProfiles;
    const handleSave = () => {
        onUpdate(section.id_e, { session_title: title, content });
        setIsEditing(false);
    };

    const handleCancel = () => {
        setTitle(section.session_title);
        setContent(section.content);
        setIsEditing(false);
    };

    const renderSectionContent = () => {
        if (isEditing) {
            if (section.type === "texto" || !section.type) {
                return (
                    <TextField
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        multiline
                        fullWidth
                        minRows={4}
                        variant="outlined"
                    />
                );
            }

            return (
                <Box
                    sx={{
                        bgcolor: "background.default",
                        p: 2,
                        borderRadius: 1,
                        textAlign: "center",
                    }}
                >
                    <Typography>
                        {section.type === "tabela_de_escopo"
                            ? "Tabela de Escopo do Projeto"
                            : "Tabela de Perfis de Atividade"}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                    >
                        Esta tabela usa dados pré-definidos do sistema
                    </Typography>
                </Box>
            );
        }

        switch (section.type) {
            case "tabela_de_escopo":
                return (
                    <ScopeTable
                        items={tableScopeData}
                        budgetData={budgetData}
                    />
                );
            case "tabela_de_perfis_de_atividade":
                return <ActivityProfileTable items={profiles} />;
            default:
                return (
                    <Typography sx={{ whiteSpace: "pre-wrap" }}>
                        {section.content}
                    </Typography>
                );
        }
    };

    const renderHeaderOptions = () => {
        const isTextSection = section.type === "texto";

        if (isTextSection)
            return (
                <>
                    {isEditing ? (
                        <>
                            <IconButton size="small" onClick={handleSave}>
                                <Save fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={handleCancel}>
                                <X fontSize="small" />
                            </IconButton>
                        </>
                    ) : (
                        <>
                            <IconButton
                                size="small"
                                onClick={() => setIsEditing(true)}
                            >
                                <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={() => onDelete(section.id_e)}
                            >
                                <Trash2 fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </>
            );

        return (
            <IconButton size="small" onClick={() => onDelete(section.id_e)}>
                <Trash2 fontSize="small" />
            </IconButton>
        );
    };

    return (
        <Card sx={{ mb: 2, borderRadius: 2 }}>
            <CardHeader
                avatar={
                    <Avatar
                        sx={{
                            bgcolor: "primary.main",
                            width: 32,
                            height: 32,
                        }}
                    >
                        {section.position}
                    </Avatar>
                }
                title={
                    isEditing ? (
                        <TextField
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                        />
                    ) : (
                        <Typography variant="h6">
                            {section.session_title}
                        </Typography>
                    )
                }
                action={
                    <Box sx={{ display: "flex" }}>
                        <IconButton
                            onClick={() => onMoveUp(section.id_e)}
                            disabled={isFirst}
                            size="small"
                        >
                            <ArrowUp fontSize="small" />
                        </IconButton>
                        <IconButton
                            onClick={() => onMoveDown(section.id_e)}
                            disabled={isLast}
                            size="small"
                        >
                            <ArrowDown fontSize="small" />
                        </IconButton>

                        {renderHeaderOptions()}
                    </Box>
                }
                sx={{
                    pb: 0,
                    "& .MuiCardHeader-action": {
                        margin: 0,
                    },
                }}
            />
            <CardContent>{renderSectionContent()}</CardContent>
        </Card>
    );
}
